import { GuestCRMAttributes, GuestCRMIntegration, dashboard } from 'types';
import { IntegrationType } from 'constants/IntegrationConstants';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { concat, map, sortBy, uniq } from 'es-toolkit/compat';
import { toEnumString } from '@tock/coretypes';
import protos from 'utils/protos';

const { ExternalSystemType } = protos;
export const COMMERCE7_URL = 'https://www.commerce7.com/partners/app?id=tock';

export function getGuestIntegrationInformation(
  integrationType: GuestCRMIntegration,
  guestIntegrationAttributes: dashboard.IExternalIntegrationAttribute[] | undefined
): GuestCRMAttributes {
  const attributesForIntegration =
    guestIntegrationAttributes?.filter(
      (attribute) => toEnumString(ExternalSystemType, attribute.type) === integrationType
    ) ?? [];

  switch (integrationType) {
    case 'SALESFORCE':
      return getIntegrationAttributesForType(attributesForIntegration, 'SALESFORCE');
    case 'BLOYAL':
      return getIntegrationAttributesForType(attributesForIntegration, 'BLOYAL');
    case 'DYNAMICS_365':
      return getIntegrationAttributesForType(attributesForIntegration, 'DYNAMICS_365');
    case 'WINEDIRECT_CLASSIC':
      return getIntegrationAttributesForType(attributesForIntegration, 'WINEDIRECT_CLASSIC');
    case 'COMMERCE7':
      return getIntegrationAttributesForType(attributesForIntegration, 'COMMERCE7');
  }
}

type IntegrationAttributeMap = Partial<{
  [Property in GuestCRMIntegration]: {
    membership: string | undefined;
    tags: string | undefined;
    notes: string | undefined;
  };
}>;

export const integrationAttributeMap: IntegrationAttributeMap = {
  COMMERCE7: {
    membership: 'MembershipClubName',
    tags: 'ContactTags',
    notes: 'GuestNotes',
  },
  WINEDIRECT_CLASSIC: {
    membership: 'MembershipClubName',
    tags: 'ContactType',
    notes: 'GuestNotes',
  },
  SALESFORCE: {
    membership: 'Membership',
    tags: undefined,
    notes: undefined,
  },
  BLOYAL: {
    membership: 'MembershipName',
    tags: undefined,
    notes: undefined,
  },
  DYNAMICS_365: {
    membership: 'CustomerAffiliation',
    tags: undefined,
    notes: 'CustomerAccount',
  },
};

const findAttributes = (
  attributes: dashboard.IExternalIntegrationAttribute[],
  attrName: string | undefined
) => (attrName ? attributes.filter((attrObj) => attrObj.attribute === attrName) : []);

const getIntegrationAttributesForType = (
  guestAttributes: dashboard.IExternalIntegrationAttribute[],
  integrationType: GuestCRMIntegration
) => {
  const foundNoteAttributes = findAttributes(
    guestAttributes,
    integrationAttributeMap[integrationType]?.notes
  );
  const foundMembershipAttributes = findAttributes(
    guestAttributes,
    integrationAttributeMap[integrationType]?.membership
  );
  const foundContactAttributes = findAttributes(
    guestAttributes,
    integrationAttributeMap[integrationType]?.tags
  );

  const extractedNote = foundNoteAttributes[0]?.stringValue ?? '';
  const extractedMemberships = foundMembershipAttributes[0]?.stringValue ?? '';
  const extractedContactTypes = foundContactAttributes[0]?.stringValue;

  return {
    note: extractedNote,
    membership: extractedMemberships,
    contactType: extractedContactTypes?.split('\n'),
  };
};

export function isConnected(
  system: dashboard.ExternalSystemType,
  connectedSystems: dashboard.IConnectedExternalSystem[] | null
): boolean {
  return !!connectedSystems && !!getConnectedSystem(system, connectedSystems);
}

export function shouldShowIntegration(system: dashboard.ConnectedExternalSystem) {
  // Aloha: Hidden when pre-onboarding is complete, so users continue connection through directory
  // BLoyal: Do not want to show as a card

  return (
    (system.system !== 'ALOHA' || system.status !== 'PENDING_STAGE_2_INTEGRATION') &&
    system.system !== 'BLOYAL'
  );
}

export function getConnectedSystem(
  system: dashboard.ExternalSystemType,
  connectedSystems: dashboard.IConnectedExternalSystem[] | null
): dashboard.ConnectedExternalSystem | undefined {
  return connectedSystems?.find(
    (connectedSystem) => connectedSystem.system === system
  ) as dashboard.ConnectedExternalSystem;
}

export function getDynamics365AccountDetails(
  guestIntegrations: dashboard.IExternalIntegrationAttribute[] | undefined
): string {
  const attributes = getGuestIntegrationInformation('DYNAMICS_365', guestIntegrations);
  const dynamics365Account = attributes.note;
  const dynamics365Affiliations = attributes.membership;
  return dynamics365Account
    ? `${dynamics365Account}${dynamics365Affiliations ? ' - ' + dynamics365Affiliations : ''}`
    : '';
}

export function getGuestGroupTagByType(
  groupTags: dashboard.IExternalGuestGroupSet[],
  groupType: dashboard.GuestGroupType
): dashboard.IExternalGuestGroup[] {
  return groupTags.find((tag) => tag.groupType === groupType)?.attribute ?? [];
}

// Merges the existing group tags with ones that are current configured to be selected.
export function getGuestGroupTagOptionsWithSelected(
  groupTags: dashboard.IExternalGuestGroupSet[],
  groupType: dashboard.GuestGroupType,
  initialSelected: string[]
): Option[] {
  return uniq(
    sortBy(concat(map(getGuestGroupTagByType(groupTags, groupType), 'name'), initialSelected))
  ).map((name) => ({
    key: name || '',
    label: name || '',
    value: name || '',
  }));
}
export function getCrmMemberships(
  attr: dashboard.IExternalIntegrationAttribute[],
  enabledIntegrations: GuestCRMIntegration[]
) {
  return enabledIntegrations.flatMap((enabledIntegration) => {
    const clubMembership = getGuestIntegrationInformation(enabledIntegration, attr).membership;
    if (clubMembership !== '') {
      return [guestCrmIntegrationTypeToLabel(enabledIntegration) + ': ' + clubMembership];
    }
    return [];
  });
}

export const guestCrmIntegrationTypeToLabel = (type: GuestCRMIntegration) => {
  switch (type) {
    case 'BLOYAL':
      return 'BLoyal';
    case 'COMMERCE7':
      return 'Commerce7';
    case 'DYNAMICS_365':
      return 'Dynamics365';
    case 'SALESFORCE':
      return 'Salesforce';
    case 'WINEDIRECT_CLASSIC':
      return 'WineDirect';
  }
};

export const getIntegrationEnumFromString = (enumStr: string) => {
  return IntegrationType[enumStr as keyof typeof IntegrationType];
};

export const integrationTypeToLabel = (type: IntegrationType | undefined) => {
  switch (type) {
    case IntegrationType.COMMERCE7:
      return 'Commerce7';
    case IntegrationType.THE_CUSTOMER_CONNECTION:
      return 'The Customer Connection';
    case IntegrationType.TRIPLESEAT_EVENTS:
      return 'Tripleseat Events';
    case IntegrationType.TRIPLESEAT_LEADS:
      return 'Tripleseat Leads';
    case IntegrationType.TOAST:
      return 'Toast';
    case IntegrationType.UNTILL:
      return 'unTill';
    case IntegrationType.LIGHTSPEED:
      return 'Lightspeed';
    case IntegrationType.WINEDIRECT_CLASSIC:
      return 'WineDirect';
    case IntegrationType.GOOGLE_ANALYTICS_WEB_PROPERTY:
      return 'Google Analytics UA GA';
    case IntegrationType.GOOGLE_ANALYTICS_WEB_PROPERTY_4:
      return 'Google Analytics GA4';
    case IntegrationType.MICROS:
      return 'Micros Simphony';
    case IntegrationType.MICROS_3700:
      return 'Micros 3700';
    case IntegrationType.POSITOUCH:
      return 'POSitouch';
    case IntegrationType.ALOHA:
      return 'Aloha';
    case IntegrationType.SQUARE_POS:
      return 'Square POS';
    default:
      return '';
  }
};

export const getHelpUrl = (type: IntegrationType | undefined): string => {
  switch (type) {
    case IntegrationType.LIGHTSPEED: {
      return 'https://tock.zendesk.com/hc/en-us/articles/360030920872-Integrating-with-Lightspeed-Restaurant-U-series-POS';
    }
    case IntegrationType.WINEDIRECT_CLASSIC: {
      return 'https://tock.zendesk.com/hc/en-us/articles/360030920852-Integrating-with-WineDirect';
    }
    case IntegrationType.TRIPLESEAT_EVENTS:
      return 'https://tock.zendesk.com/hc/en-us/articles/30526519124500-Tripleseat-Events-Integration-Overview';
    case IntegrationType.TRIPLESEAT_LEADS: {
      return 'https://tock.zendesk.com/hc/en-us/articles/360032808631-Integrating-with-Tripleseat';
    }
    case IntegrationType.THE_CUSTOMER_CONNECTION: {
      return 'https://tock.zendesk.com/hc/en-us';
    }
    case IntegrationType.COMMERCE7: {
      return 'https://tock.zendesk.com/hc/en-us/articles/4404443059348-Integrating-with-Commerce7';
    }
    default:
      return '';
  }
};

export const getIntegrationLink = (system: IntegrationType | undefined) => {
  switch (system) {
    case IntegrationType.ACTIV8:
      return 'https://tock.zendesk.com/hc/en-us/articles/22977881359636-Integrating-with-Activ8';
    case IntegrationType.BLOOM_INTELLIGENCE:
      return 'https://tock.zendesk.com/hc/en-us/articles/30707346447252-Bloom-Intelligence-Marketing-Integration';
    case IntegrationType.COMMERCE7:
      return 'https://tock.zendesk.com/hc/en-us/articles/4404443059348-Integrating-with-Commerce7';
    case IntegrationType.ECELLAR:
      return 'https://tock.zendesk.com/hc/en-us/articles/28273142792212-eCELLAR-Wine-Commerce-Integration';
    case IntegrationType.GOOGLE_ANALYTICS_WEB_PROPERTY:
    case IntegrationType.GOOGLE_ANALYTICS_WEB_PROPERTY_4:
      return 'https://tock.zendesk.com/hc/en-us/articles/360031261191-Setting-up-Google-Analytics';
    case IntegrationType.LIGHTSPEED:
      return 'https://tock.zendesk.com/hc/en-us/articles/360030920872-Integrating-with-Lightspeed-Restaurant-U-series-POS';
    case IntegrationType.MICROS:
    case IntegrationType.MICROS_3700:
      return 'https://tock.zendesk.com/hc/en-us/articles/23959116291604-Micros-Simphony-POS-Integration-Overview';
    case IntegrationType.ORDERPORT:
      return 'https://tock.zendesk.com/hc/en-us/articles/23996166133908-OrderPort-Wine-Commerce-Integration';
    case IntegrationType.REDCHIRP:
      return 'https://tock.zendesk.com/hc/en-us/articles/26554207732500-RedChirp-Text-Messaging-Integration';
    case IntegrationType.THE_CUSTOMER_CONNECTION:
      return 'https://tock.zendesk.com/hc/en-us';
    case IntegrationType.TOAST:
      return 'https://tock.zendesk.com/hc/en-us/articles/360047598451-Toast-Integration-Overview ';
    case IntegrationType.TRIPLESEAT_EVENTS:
      return 'https://tock.zendesk.com/hc/en-us/articles/30526519124500-Tripleseat-Events-Integration-Overview';
    case IntegrationType.TRIPLESEAT_LEADS:
      return 'https://tock.zendesk.com/hc/en-us/articles/360032808631-Integrating-with-Tripleseat';
    case IntegrationType.WINEDIRECT_CLASSIC:
      return 'https://tock.zendesk.com/hc/en-us/articles/360030920852-Integrating-with-WineDirect';
    default:
      return '';
  }
};

export const getLabelFromEnum = (status: dashboard.IntegrationStatus): string => {
  switch (status) {
    case 'PENDING_INTEGRATION':
      return 'Pending';
    case 'ACTIVE_INTEGRATION':
      return 'Active';
    case 'PAUSED_INTEGRATION':
      return 'Paused';
    case 'PENDING_STAGE_2_INTEGRATION':
      return 'Ready to Connect';
    default:
      assertNever(status);
  }
};

export const getColorFromEnum = (status: dashboard.IntegrationStatus) => {
  switch (status) {
    case 'PENDING_INTEGRATION':
      return 'warning';
    case 'ACTIVE_INTEGRATION':
      return 'success';
    case 'PAUSED_INTEGRATION':
      return 'warning';
    case 'PENDING_STAGE_2_INTEGRATION':
      return 'primary';
    default:
      assertNever(status);
  }
};
